import React, { FC, useRef, useImperativeHandle, useEffect } from "react";
import {
  ValidatorComponentProps,
  TextValidator,
  SelectValidator
} from "react-material-ui-form-validator";
import RHDropdownIcon from "icon-dropdown";
import { FormHelperText } from "@mui/material";
import { useInitialState } from "hooks-use-initial-state";
import { useEnv } from "@RHCommerceDev/hooks/useEnv";
import yn from "yn";

export type BlurValidatorProps = ValidatorComponentProps & {
  errors?: string | boolean;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
};

export const BlurTextValidator: FC<BlurValidatorProps> = React.forwardRef(
  ({ onBlur, ...props }, ref) => {
    const [value, setValue] = useInitialState<string>(props?.value || "");
    const validatorRef = useRef<TextValidator>(null);
    useImperativeHandle(ref, () => ({
      validate: () => (validatorRef as any).current?.validate(value.trim())
    }));

    const handleBlurValidate = (event: React.FocusEvent<HTMLInputElement>) => {
      (validatorRef as any).current?.validate(value.trim(), true);
      if (onBlur) onBlur(event);
    };

    return (
      <TextValidator
        {...props}
        onChange={handleChange}
        value={value}
        onBlur={handleBlurValidate}
        ref={validatorRef}
        onKeyDown={e => {
          e.stopPropagation();
        }}
      >
        {props.children}
      </TextValidator>
    );

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
      setValue(event.target.value);
      props.onChange?.(event);
    }
  }
);

export const BlurSelectValidator: FC<BlurValidatorProps> = React.forwardRef(
  (props, ref) => {
    const [value, setValue] = useInitialState<string>(props?.value || "");
    const validatorRef = useRef<SelectValidator>(null);
    const env = useEnv();
    const FEATURE_EU_MONACO = yn(env.FEATURE_EU_MONACO);

    useEffect(() => {
      if (
        FEATURE_EU_MONACO &&
        (props.name === "country" || props.name === "state")
      )
        setValue(props.value);
    }, [FEATURE_EU_MONACO, props.value, setValue]);

    useImperativeHandle(ref, () => ({
      validate: () => (validatorRef as any).current?.validate(value),
      setValue: (value: any) => setValue(value)
    }));

    const handleBlurValidate = (event: React.FocusEvent<HTMLInputElement>) => {
      (validatorRef as any).current?.validate(value, true);
    };

    return (
      <div id={"component-input-validation"}>
        <SelectValidator
          {...props}
          SelectProps={{
            IconComponent: RHDropdownIcon,
            ...props.SelectProps,
            native: true
          }}
          onChange={handleChange}
          value={value}
          onBlur={handleBlurValidate}
          ref={validatorRef}
        >
          {props.children}
        </SelectValidator>
        {props.errors && (
          <FormHelperText error={true}>{props.errors}</FormHelperText>
        )}
      </div>
    );

    function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
      setValue(event.target.value);
      props.onChange?.(event);
    }
  }
);
